<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import { JobOccurrenceFilterOptionsDto } from '@/api/generator';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import ToggleButton from '@/components/buttons/ToggleButton.vue';
import { DashboardFilterObject, DashboardJobGroupOption, DashboardTimeRangeOption } from '../types';

const { t } = useTranslation();

const props = withDefaults(
  defineProps<{
    modelValue: DashboardFilterObject;
    filterOptions?: JobOccurrenceFilterOptionsDto;
  }>(),
  {
    filterOptions: undefined,
  },
);

const emits = defineEmits<{
  'update:modelValue': [filterObject: DashboardFilterObject];
}>();

const options = computed(() => {
  return {
    targetSystemIds: props.filterOptions?.targetSystems?.map((targetSystem) => ({
      title: targetSystem.name || '',
      value: targetSystem.id,
    })),

    stateGroup: (['open', 'review'] satisfies DashboardJobGroupOption[]).map((option) => ({
      title: t(`JobGroup.${option}`),
      value: option,
    })),

    timeRange: (['today', 'next7days', 'next30days'] satisfies DashboardTimeRangeOption[]).map(
      (option) => ({
        title: t(`TimeRangeFilterOption.${option}`),
        value: option,
      }),
    ),
  };
});

function updateFilter(filterChanges: Partial<DashboardFilterObject>) {
  emits('update:modelValue', { ...props.modelValue, ...filterChanges });
}

function isDashboardTimeRangeOption(value: string): value is DashboardTimeRangeOption {
  return ['today', 'next7days', 'next30days'].includes(value);
}

const isSubFilterVisible = computed(() => !!props.filterOptions?.targetSystems);
</script>

<template>
  <v-row v-if="isSubFilterVisible" class="align-end mt-2">
    <v-col cols="12" sm="6" md="5" lg="3">
      <Autocomplete
        :modelValue="modelValue.targetSystemIds"
        :label="$t('MedicalSystem_other')"
        :items="options.targetSystemIds"
        multiple
        chips
        closableChips
        emptyAsUndefined
        @update:modelValue="updateFilter({ targetSystemIds: $event })"
      ></Autocomplete>
    </v-col>

    <v-col v-if="modelValue.stateGroup === 'open'">
      <ToggleButton
        :modelValue="modelValue.timeRange"
        :options="options.timeRange"
        mandatory
        divided
        @update:modelValue="
          (val) => isDashboardTimeRangeOption(val) && updateFilter({ timeRange: val })
        "
      />
    </v-col>
  </v-row>
</template>
